import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
} from "@angular/core";
import { AnchorMenuStore } from "../core/sim/anchor-menu.store";
import {
  CREW_CLEATS,
  CrewCommandsStore,
} from "../core/sim/crew-commands.store";

/**
 * In-world contextual menu shown when the player taps a mooring
 * anchor (bollard or buoy) in the 3D scene.
 *
 * For the tapped anchor the menu lists every player cleat with a
 * single "Cumuj <cleat> tutaj" action. The action is enabled only
 * when {@link CrewCommandsStore.assignToAnchor} would succeed (boat
 * hook in reach for buoys, shore party present for bollards). A
 * disabled row carries a tooltip explaining why.
 */
@Component({
  selector: "po-anchor-context-menu",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./anchor-context-menu.component.html",
  styleUrl: "./anchor-context-menu.component.css",
})
export class AnchorContextMenuComponent {
  readonly store = inject(AnchorMenuStore);
  private readonly crew = inject(CrewCommandsStore);

  readonly request = this.store.request;

  readonly cleatRows = computed(() => {
    const req = this.request();
    if (!req) return [];
    const lines = this.crew.lines();
    return CREW_CLEATS.map((cleat) => {
      const line = lines[cleat.key];
      const canAssign = this.crew
        ? this.canAssign(cleat.key, req.pick.anchorId, req.pick.kind)
        : false;
      const hint = canAssign
        ? `Załoga zacumuje ${cleat.label} do ${req.pick.anchorId}`
        : this.crew.assignmentBlockReason(
            cleat.key,
            req.pick.anchorId,
            req.pick.kind,
          );
      const isCurrentTarget =
        line?.target === req.pick.anchorId && line?.state === "made_fast";
      const tightenReason = isCurrentTarget
        ? this.crew.adjustBlockReason(cleat.key, -1)
        : "";
      const slackReason = isCurrentTarget
        ? this.crew.adjustBlockReason(cleat.key, +1)
        : "";
      return {
        cleat,
        canAssign,
        hint,
        lineState: line?.state ?? "stowed",
        lineLabel: line?.target === req.pick.anchorId ? "← obecny cel" : "",
        isCurrentTarget,
        canTighten: isCurrentTarget && tightenReason === "",
        canSlack: isCurrentTarget && slackReason === "",
        tightenHint: tightenReason || "Skróć cumę o 1 m",
        slackHint: slackReason || "Wypuść cumę o 1 m",
        restLen: (line?.restLength ?? 0).toFixed(1),
      };
    });
  });

  kindLabel(k: "buoy" | "bollard"): string {
    return k === "buoy" ? "Bojka" : "Poller";
  }

  onAssign(cleatKey: string): void {
    const req = this.request();
    if (!req) return;
    // Suggested rest length: distance camera→anchor isn't useful;
    // cleat→anchor world distance is. Cheap heuristic: use 1.2× of
    // the player-cleat-to-anchor distance with a 1.5 m floor. The
    // store has no direct cleat-world helper, so we let the caller
    // pre-compute the rest length later; for now leave default.
    const restLength = 8;
    const ok = this.crew.assignToAnchor(
      cleatKey,
      req.pick.anchorId,
      req.pick.kind,
      restLength,
    );
    if (ok) this.store.close();
  }

  /**
   * Shorten / pay out a made-fast line by `deltaM` metres. Crew
   * physically hauls or releases the rope; the boat is pulled in or
   * lets out gradually because the line is a damped spring.
   */
  onAdjust(cleatKey: string, deltaM: number): void {
    this.crew.adjustLineLength(cleatKey, deltaM);
  }

  /** Esc closes the menu, just like clicking outside. */
  @HostListener("document:keydown.escape")
  onEsc(): void {
    this.store.close();
  }

  /** Click anywhere on the page (other than the menu) closes it. */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.request()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest(".menu")) return;
    if (target?.closest("canvas#gpu-canvas")) return; // canvas itself
    this.store.close();
  }

  private canAssign(
    cleat: string,
    anchorId: string,
    kind: "buoy" | "bollard",
  ): boolean {
    return this.crew.assignmentBlockReason(cleat, anchorId, kind) === "";
  }
}
